import { useParams, useNavigate, generatePath } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import { useUpdateHealthClaim } from '@/services/healthClaim/useUpdateHealthClaim'
import { useCreateHealthClaim } from '@/services/healthClaim/useCreateHealthClaim'
import healthClaimFormAtom from '@/recoil/healthClaimForm'
import { HealthClaimFormInput, InterestCode, InterestTypeEnum } from '@/services/healthClaim/types'
import message from '@/libs/message'
import CONSTANT from '@/constant'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'
import { useTreatmentType } from '@/services/healthClaim/useTreatmentType'
import dayjs from 'dayjs'
import { DocumentContext } from '../HealthClaimDetailPage'
import { useContext, useEffect, useState, useCallback } from 'react'
import { DocumentItem } from '../SupportingDocumentsForClaimTabDocument/types'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { useCreateRequestDocument } from '@/services/healthClaim/useCreateRequestDocument'
import { DocumentRequest } from '@/services/healthClaim/useCreateRequestDocument/types'
import { useQueryClient } from '@tanstack/react-query'
import {
  INCOMPLETE_COVERAGE_DATA_ERROR_CODE,
  showClaimErrorNotification,
} from '@/libs/utils/claim-error-notification.util'
import hasUpdatedDocumentStatusAtom from '@/recoil/hasUpdatedDocumentStatus/atom'

export const SaveButtonHook = () => {
  const navigate = useNavigate()
  const { claimFormId } = useParams<{ claimFormId?: string }>()
  const [healthClaim, setHealthClaim] = useRecoilState(healthClaimFormAtom)
  const hasUpdatedDocumentStatus = useRecoilValue(hasUpdatedDocumentStatusAtom)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [requiredModalType, setRequiredModalType] = useState<'medicalExpense' | 'documents' | null>(
    null,
  )
  const {
    mutateAsync: updateHealthClaim,
    isPending: isUpdatePending,
    isSuccess: isUpdateSuccess,
    error: updateError,
    isError: isUpdateError,
  } = useUpdateHealthClaim()
  const {
    mutateAsync: createHealthClaim,
    isPending: isCreatePending,
    isSuccess: isCreateSuccess,
    error: createError,
    isError: isCreateError,
  } = useCreateHealthClaim()
  const {
    getAllFormValues,
    validateAllForms,
    isNewPolicyType,
    isCoverageDataIncomplete,
    hasEditedOnce,
    selectedPolicy,
  } = useHealthClaimForm()
  const documents = useContext(DocumentContext)
  const isReclaimMode = healthClaim.reclaim
  const isCreateMode = healthClaim.mode === HealthClaimModeEnum.CREATE
  const isEditMode = !isCreateMode && !isReclaimMode
  // Require at least one document to exist across all buckets
  const hasAtLeastOneDocument = documents.some(
    (doc) => doc.claimDocuments.length + doc.uploadClaimDocuments.length > 0,
  )
  // Disable save until there is an edit (or requested documents) in create/edit flows
  const requiresEditBeforeSave = (isCreateMode && !isReclaimMode) || isEditMode
  const isSaveDisabled =
    requiresEditBeforeSave &&
    !hasEditedOnce &&
    !healthClaim.hasRequestedDocuments &&
    !hasUpdatedDocumentStatus

  const treatmentTypesQuery = useTreatmentType({ policyNumber: selectedPolicy?.policyNumber || '' })
  const { mutateAsync: mutateRequestDocument } = useCreateRequestDocument()
  const queryClient = useQueryClient()

  const getTreatmentTypeFlags = useCallback(
    (treatmentTypeId?: string) => {
      const fallbackFlags = { isAccident: false, isIpd: false, isHib: false }
      if (!treatmentTypeId) return fallbackFlags

      const name = treatmentTypesQuery.data?.find((t) => t.id === treatmentTypeId)?.name || ''
      if (!name) return fallbackFlags

      const lower = name.toLowerCase()
      const hasAccident = lower.includes('อุบัติเหตุ')
      const hasIpd = lower.includes('ipd') || lower.includes('ผู้ป่วยใน')
      const hasOpd = lower.includes('opd') || lower.includes('ผู้ป่วยนอก')
      const isAccident = hasAccident && !hasIpd
      const isOpd = hasOpd && !hasAccident
      const isIpd = lower.includes('ipd') || lower.includes('ผู้ป่วยใน')
      const isHib = lower.includes('ชดเชยรายได้')

      return { isAccident, isOpd, isIpd, isHib }
    },
    [treatmentTypesQuery.data],
  )

  // Helper function to process medical expense data based on policy type
  const processMedicalExpenseData = (medicalExpenseData: any) => {
    if (!medicalExpenseData?.medicalExpenses?.length) {
      return {
        interestType: InterestTypeEnum.OLD,
        interestCodes: [],
        claimTypes: [],
      }
    }

    // Prefer the policy type determined by context; fallback to structural check
    const resolvedIsNewPolicyType =
      isNewPolicyType !== null
        ? isNewPolicyType
        : medicalExpenseData.medicalExpenses.some(
            (item: any) =>
              'maxClaimAmount' in item ||
              'remainingClaimBalance' in item ||
              item.maxClaimAmount !== undefined ||
              item.remainingClaimBalance !== undefined,
          )

    if (resolvedIsNewPolicyType) {
      // Process as Interest Codes (new policy type)
      const interestCodes = medicalExpenseData.medicalExpenses
        .filter((item: any) => item.receiptAmount > 0 || item.coverageAmount > 0)
        .map(
          (item: any) =>
            ({
              maxClaimAmount: Number(item.maxClaimAmount) || 0,
              remainingClaimBalance: Number(item.remainingClaimBalance) || 0,
              interestCode: item.code,
              receiptAmount: Number(item.receiptAmount) || 0,
              coverageAmount: Number(item.coverageAmount) || 0,
            } as InterestCode),
        )

      return {
        interestType: InterestTypeEnum.NEW,
        interestCodes,
        claimTypes: [],
      }
    } else {
      // Process as Claim Types (old policy type)
      const claimTypes = medicalExpenseData.medicalExpenses
        .filter((item: any) => item.coverageAmount > 0)
        .map((item: any) => ({
          id: item.key,
          coverageAmount: Number(item.coverageAmount) || 0,
        }))

      return {
        interestType: InterestTypeEnum.OLD,
        interestCodes: [],
        claimTypes,
      }
    }
  }

  const mapAndMergeDocuments = (documents: DocumentItem[]) => {
    return documents.flatMap((doc) => {
      const claimDocuments = doc.claimDocuments
        .filter((c) => !!c.id)
        .map((c) => ({
          documentId: c.id,
        }))
      const uploadClaimDocuments = doc.uploadClaimDocuments
        .filter((u) => !!u.id)
        .map((u) => ({
          documentId: u.id,
        }))

      return [...claimDocuments, ...uploadClaimDocuments]
    })
  }

  // Function to submit pending document requests after claim form is created/updated
  const submitPendingRequestDocuments = async (targetClaimFormId: string) => {
    const pendingRecords = healthClaim.pendingRequestDocuments ?? []
    if (!pendingRecords.length) return

    for (const record of pendingRecords) {
      const documentRequests: DocumentRequest[] = (record.documentDetails || [])
        .filter((detail) => !!detail.documentTypeId)
        .map((detail) => ({
          documentTypeCode: detail.documentTypeId,
          requestDetail: detail.requestDetail || '',
        }))

      if (!documentRequests.length) continue

      await mutateRequestDocument({
        claimFormId: targetClaimFormId,
        phoneNumber: record.phoneNumber,
        documentRequests,
      })
    }

    setHealthClaim((prev) => ({
      ...prev,
      pendingRequestDocuments: [],
    }))

    await queryClient.invalidateQueries({ queryKey: ['useRequestDocumentList', targetClaimFormId] })
  }

  const handleCreateOrUpdateHealthClaim = async (formData: HealthClaimFormInput) => {
    if (isReclaimMode || isCreateMode) {
      // For reclaim mode and create mode, we use create API and don't include claimFormId
      const { claimFormId: _, ...createFormData } = formData
      const result = await createHealthClaim(createFormData)
      return result
    } else {
      // For update mode, we use update API with claimFormId
      const result = await updateHealthClaim(formData)
      return result
    }
  }

  const processClaimForm = async () => {
    setIsModalOpen(true)
  }
  const executeClaimFormProcess = async () => {
    if (!isReclaimMode && !isCreateMode && (!claimFormId || !healthClaim.data)) {
      message.error('ไม่พบข้อมูลเคลม กรุณาลองใหม่อีกครั้ง')
      return
    }

    if ((isReclaimMode || isCreateMode) && !healthClaim.data) {
      message.error('ไม่พบข้อมูลเคลม กรุณาลองใหม่อีกครั้ง')
      return
    }

    try {
      // Validate all forms before submission
      const isValid = await validateAllForms()
      if (!isValid) {
        message.error('กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง')
        return
      }

      // Collect form data from all form components
      const {
        insuredPersonData,
        treatmentDetailData,
        compensationRequestData,
        medicalExpenseData,
      } = getAllFormValues()

      // Resolve treatment type category for conditional payload fields
      const { isAccident, isIpd, isHib } = getTreatmentTypeFlags(
        treatmentDetailData.treatmentTypeId,
      )

      if (isCoverageDataIncomplete) {
        showClaimErrorNotification({
          response: { data: { code: INCOMPLETE_COVERAGE_DATA_ERROR_CODE } },
        })
        return
      }

      // Process medical expense data to determine policy type and format data
      const medicalExpenseInfo = processMedicalExpenseData(medicalExpenseData)

      // Validate that at least some medical expense data is provided
      if (
        medicalExpenseInfo.interestType === InterestTypeEnum.NEW &&
        medicalExpenseInfo.interestCodes.length === 0
      ) {
        setRequiredModalType('medicalExpense')
        return
      }

      if (
        medicalExpenseInfo.interestType === InterestTypeEnum.OLD &&
        medicalExpenseInfo.claimTypes.length === 0
      ) {
        setRequiredModalType('medicalExpense')
        return
      }

      // Validate that at least one document exists across all buckets
      if (!hasAtLeastOneDocument) {
        setRequiredModalType('documents')
        return
      }

      const previousClaimFormId = isReclaimMode
        ? claimFormId || healthClaim.data?.claimFormId
        : undefined

      const formData: HealthClaimFormInput = {
        claimFormId: !isReclaimMode && !isCreateMode ? claimFormId : undefined,
        previousClaimFormId,
        policyNumber:
          selectedPolicy?.policyNumber || healthClaim.data.policyDetails?.[0]?.policyNumber || '',
        email: insuredPersonData.email || undefined,
        mobileNumber: insuredPersonData.phoneNumber || '',
        treatmentTypeId: treatmentDetailData.treatmentTypeId || '',
        treatmentDateIn: treatmentDetailData.treatmentDateIn.format('YYYY-MM-DD'),
        // Only include treatmentDateOut for IPD or HIB
        treatmentDateOut:
          (isIpd || isHib) && treatmentDetailData.treatmentDateOut
            ? treatmentDetailData.treatmentDateOut.format('YYYY-MM-DD')
            : undefined,
        hospitalId: treatmentDetailData.isOtherHospital
          ? undefined
          : treatmentDetailData.hospitalId,
        isOtherHospital: treatmentDetailData.isOtherHospital || false,
        otherHospitalName: treatmentDetailData.isOtherHospital
          ? treatmentDetailData.otherHospitalName
          : undefined,
        diseaseId: treatmentDetailData.diseaseId || undefined,
        // Include accident fields only for accident types
        accidentLocation: isAccident
          ? treatmentDetailData.accidentLocation || undefined
          : undefined,
        accidentDate:
          isAccident && treatmentDetailData.accidentDate
            ? treatmentDetailData.accidentDate.format('YYYY-MM-DD')
            : undefined,
        accidentTime:
          isAccident && treatmentDetailData.accidentTime
            ? dayjs(treatmentDetailData.accidentTime).format('HH:mm')
            : undefined,
        accidentDetails: isAccident ? treatmentDetailData.accidentDetails || undefined : undefined,
        treatmentAmount: compensationRequestData.treatmentAmount
          ? Number(compensationRequestData.treatmentAmount)
          : undefined,
        receiptNumber: compensationRequestData.receiptNumber || '',
        approvedClaimAmount: compensationRequestData.approvedClaimAmount || undefined,
        interestType: medicalExpenseInfo.interestType,
        interestCodes: medicalExpenseInfo.interestCodes,
        claimTypes: medicalExpenseInfo.claimTypes,
        reimbursementMethod: compensationRequestData.reimbursementMethod || 'BANK',
        bankId:
          compensationRequestData.reimbursementMethod === 'BANK'
            ? compensationRequestData.bankProvider
            : undefined,
        bankAccountNumber:
          compensationRequestData.reimbursementMethod === 'BANK'
            ? compensationRequestData.bankAccountNumber
            : undefined,
        bankAccountName:
          compensationRequestData.reimbursementMethod === 'BANK'
            ? compensationRequestData.bankAccountName
            : undefined,
        bankBranch:
          compensationRequestData.reimbursementMethod === 'BANK'
            ? compensationRequestData.bankBranchName
            : undefined,
        isUserPolicyAddress: compensationRequestData.isUserPolicyAddress || false,
        compensationPersonName:
          compensationRequestData.reimbursementMethod === 'POSTALCHECK'
            ? compensationRequestData.compensationPersonName
            : undefined,
        compensationPersonPhone:
          compensationRequestData.reimbursementMethod === 'POSTALCHECK'
            ? compensationRequestData.compensationMobileNumber
            : undefined,
        compensationAddress:
          compensationRequestData.reimbursementMethod === 'POSTALCHECK'
            ? compensationRequestData.compensationAddress
            : undefined,
        compensationProvinceCode:
          compensationRequestData.reimbursementMethod === 'POSTALCHECK'
            ? compensationRequestData.province
            : undefined,
        compensationDistrictCode:
          compensationRequestData.reimbursementMethod === 'POSTALCHECK'
            ? compensationRequestData.district
            : undefined,
        compensationSubDistrictCode:
          compensationRequestData.reimbursementMethod === 'POSTALCHECK'
            ? compensationRequestData.subdistrict
            : undefined,
        compensationPostCode:
          compensationRequestData.reimbursementMethod === 'POSTALCHECK'
            ? compensationRequestData.postal
            : undefined,
        documents: mapAndMergeDocuments(documents),
      }

      const result = await handleCreateOrUpdateHealthClaim(formData)

      const nextClaimFormId = isReclaimMode || isCreateMode ? result?.claimFormId : claimFormId

      if (nextClaimFormId) {
        try {
          await submitPendingRequestDocuments(nextClaimFormId)
        } catch (error) {
          console.error('Failed to submit pending document requests:', error)
          message.error('ส่งคำขอเอกสารเพิ่มเติมไม่สำเร็จ')
          throw error
        }

        // Ensure detail is fresh when navigating
        await queryClient.refetchQueries({
          queryKey: ['useGetClaimDetail', nextClaimFormId],
        })
      }

      // Navigate back to view mode
      if ((isReclaimMode || isCreateMode) && result?.claimFormId) {
        // For reclaim mode and create mode, navigate to the newly created claim
        const nextPath = CONSTANT.ROUTES.HEALTH_CLAIM_VIEW_PAGE
        navigate(generatePath(nextPath, { claimFormId: result.claimFormId }))
      } else {
        // For update mode, navigate back to the current claim
        const nextPath = CONSTANT.ROUTES.HEALTH_CLAIM_VIEW_PAGE
        navigate(generatePath(nextPath, { claimFormId }))
      }
    } catch (error: any) {
      console.error('Error processing claim form:', error)
      // Error handling is managed in useEffect hooks
    }
  }

  const handleModalConfirm = async () => {
    setIsModalOpen(false)
    await executeClaimFormProcess()
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const handleRequiredModalClose = () => {
    setRequiredModalType(null)
  }

  const getSuccessMessage = (
    isCreateSuccess: boolean,
    isUpdateSuccess: boolean,
    hasRequestedDocuments: boolean,
  ) => {
    if (isCreateSuccess && hasRequestedDocuments) {
      return 'สร้างเคสและส่ง SMS สำเร็จ'
    }

    return isUpdateSuccess ? 'บันทึกข้อมูลสำเร็จ' : 'สร้างเคสสำเร็จ'
  }

  const showSuccessNotification = useCallback(
    (isCreateSuccess: boolean, isUpdateSuccess: boolean, hasRequestedDocuments: boolean) => {
      const successMessage = getSuccessMessage(
        isCreateSuccess,
        isUpdateSuccess,
        hasRequestedDocuments,
      )
      message.success({ content: successMessage, duration: 10 })
    },
    [],
  )

  // Show success notification after create or update success
  useEffect(() => {
    if (isUpdateSuccess || isCreateSuccess) {
      showSuccessNotification(isCreateSuccess, isUpdateSuccess, healthClaim.hasRequestedDocuments)
    }
  }, [isUpdateSuccess, isCreateSuccess, healthClaim.hasRequestedDocuments, showSuccessNotification])

  // Show error notification if create or update fails
  useEffect(() => {
    if (createError && isCreateError) {
      showClaimErrorNotification(createError)
    }

    if (updateError && isUpdateError) {
      showClaimErrorNotification(updateError)
    }
  }, [createError, isCreateError, updateError, isUpdateError])

  return {
    processClaimForm,
    isLoading: isUpdatePending || isCreatePending,
    isModalOpen,
    isCreateMode,
    isEditMode,
    hasEditedOnce,
    requiredModalType,
    handleModalConfirm,
    handleModalClose,
    handleRequiredModalClose,
    isSaveDisabled,
  }
}
